import React from 'react';
import clsx from 'clsx';

export type SegmentOption<T extends string> = T | { value: T; label: React.ReactNode };

export interface SegmentedControlProps<T extends string> {
    options: SegmentOption<T>[];
    value: T;
    onChange: (value: T) => void;
    fullWidth?: boolean;
    className?: string;
    'aria-label'?: string;
}

/** 44px plum-on-sunken segmented control. */
export function SegmentedControl<T extends string>({ options, value, onChange, fullWidth = false, className, ...aria }: SegmentedControlProps<T>) {
    const norm = options.map((o) => (typeof o === 'string' ? { value: o, label: o as React.ReactNode } : o));
    return (
        <div role="tablist" className={clsx('inline-flex items-stretch gap-0.5 rounded-pill bg-surface-sunken p-1', fullWidth && 'flex w-full', className)} {...aria}>
            {norm.map((o) => {
                const active = o.value === value;
                return (
                    <button
                        key={o.value}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        onClick={() => onChange(o.value)}
                        className={clsx(
                            'flex-1 whitespace-nowrap rounded-pill px-3 lg:px-[18px] py-[9px] text-sm font-bold transition-colors duration-200',
                            active ? 'bg-primary-500 text-white shadow-sm' : 'text-ink-secondary hover:text-ink-primary',
                        )}
                    >
                        {o.label}
                    </button>
                );
            })}
        </div>
    );
}
export default SegmentedControl;
